import Constant from '@/common/constant/Constant';
import Utils from '@/common/utils/utils';
import { tr } from '@/common/utils/tr';

/**
 * Matches the name a CUSTOM view gets when it is created: the fixed word followed by an
 * index - 'ビュー 1', 'ビュー2', or plain 'ビュー' (see generateNameForNewView).
 */
const VIEW_AUTO_NAME_PATTERN = new RegExp(`^${Constant.VIEW_NAME_DEFAULT}\\s*(\\d*)$`);

/**
 * Name of a view as it is stored, i.e. the untranslated Japanese source text.
 *
 * Use this - never getViewDisplayName() - whenever the name feeds an edit input: what the
 * user leaves in the field is written straight back to the record, so a translated name
 * would silently overwrite the source text.
 *
 * @param {Object} view view record (needs `name`)
 * @returns {string}
 */
export const getViewOriginName = view => Utils.convertJSONDataToObject(view?.name)?.jp?.text ?? '';

/**
 * Name of a view as it should be displayed.
 *
 * Views fall into three cases:
 *   - DEFAULT (system view, created by a migration - e.g. 新着ナレッジ): a fixed system
 *     label, so the whole name is translated.
 *   - CUSTOM still carrying its auto-generated name ('ビュー N'): only the fixed word is
 *     translated and the index is kept, so the dictionary holds a single entry for
 *     'ビュー' instead of one per number.
 *   - CUSTOM renamed by the user: an identifier the user chose - never translated, so
 *     they can still refer to it (e.g. when instructing the AI).
 *
 * @param {Object} view view record (needs `name` and `viewType`)
 * @returns {string}
 */
export const getViewDisplayName = view => {
    const rawName = getViewOriginName(view);

    if (view?.viewType === Constant.VIEW_TYPE.DEFAULT) {
        return tr(rawName);
    }

    const autoName = rawName.match(VIEW_AUTO_NAME_PATTERN);
    if (autoName) {
        const index = autoName[1];
        const translatedWord = tr(Constant.VIEW_NAME_DEFAULT);
        return index ? `${translatedWord} ${index}` : translatedWord;
    }

    return rawName;
};

const ViewUtils = {
    getViewDisplayName,
    getViewOriginName
};

export default ViewUtils;
